import React, { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';

export interface TabItem {
  id: string;
  label: string;
  content: React.ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  defaultId?: string;
  className?: string;
  variant?: 'pill' | 'underline';
  colorScheme?: 'default' | 'orange';
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultId,
  className,
  variant = 'pill',
  colorScheme = 'orange',
}) => {
  const [activeId, setActiveId] = useState(defaultId ?? tabs[0]?.id ?? '');
  const indicatorRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const isOrange = colorScheme === 'orange';

  const activeTab = tabs.find((t) => t.id === activeId) ?? tabs[0];

  useEffect(() => {
    const activeEl = navRef.current?.querySelector('[data-active="true"]');
    const indicator = indicatorRef.current;
    if (!activeEl || !indicator) return;
    const parent = navRef.current;
    if (!parent) return;
    const left = (activeEl as HTMLElement).offsetLeft;
    const width = (activeEl as HTMLElement).offsetWidth;
    indicator.style.transform = `translateX(${left}px)`;
    indicator.style.width = `${width}px`;
  }, [activeId, tabs]);

  return (
    <div className={clsx('w-full max-w-2xl mx-auto', className)}>
      <div
        ref={navRef}
        className={clsx(
          'relative flex rounded-2xl p-1.5',
          isOrange
            ? 'bg-orange-100/70 dark:bg-gray-800 border border-orange-200/60 dark:border-gray-700'
            : variant === 'pill' && 'bg-app-surface-muted/80 dark:bg-gray-800/80',
          variant === 'underline' && 'border-b border-app-border dark:border-gray-700'
        )}
        role="tablist"
      >
        {variant === 'pill' && (
          <div
            ref={indicatorRef}
            className={clsx(
              'absolute top-1.5 bottom-1.5 rounded-xl shadow-md transition-all duration-300 ease-out',
              isOrange ? 'bg-orange-500' : 'bg-app-surface dark:bg-gray-700'
            )}
            style={{ left: 6 }}
            aria-hidden
          />
        )}
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeId === t.id}
            aria-controls={`tabpanel-${t.id}`}
            id={`tab-${t.id}`}
            data-active={activeId === t.id}
            onClick={() => setActiveId(t.id)}
            className={clsx(
              'relative z-10 flex-1 px-4 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-colors',
              activeId === t.id
                ? isOrange
                  ? 'text-white'
                  : 'text-primary-600 dark:text-primary-400'
                : isOrange
                ? 'text-gray-700 dark:text-gray-300 hover:text-orange-600'
                : 'text-app-text-muted dark:text-gray-400 hover:text-app-text dark:hover:text-white',
              variant === 'underline' &&
                activeId === t.id &&
                'border-b-2 border-orange-500 -mb-px'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div
        id={activeTab ? `tabpanel-${activeTab.id}` : undefined}
        role="tabpanel"
        aria-labelledby={activeTab ? `tab-${activeTab.id}` : undefined}
        className={clsx(
          'mt-6 p-6 sm:p-8 rounded-3xl min-h-[140px] transition-all duration-300',
          isOrange
            ? 'bg-white dark:bg-gray-900 border border-orange-200/80 dark:border-gray-800 shadow-md shadow-orange-500/5'
            : 'bg-app-surface dark:bg-gray-800/80 border border-app-border dark:border-gray-700'
        )}
      >
        {activeTab?.content}
      </div>
    </div>
  );
};
